import { logger } from '@/services/logger'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import isEqual from 'lodash/isEqual'
import {
  getCompanyTransactionsByCompanyId,
  type CompanyTransaction,
  type CompanyTransactionsRequestBody
} from '@/services/company'
import { useEmployeeStore } from '@/stores/employee'
import axios from 'axios'

export const useCompanyTransactionsStore = defineStore('company-transactions', () => {
  const companyTransactions = ref<CompanyTransaction[]>([])
  const getCompanyTransactionsPendingPromise = ref<Promise<void> | null>(null)
  const lastQueryParams = ref<CompanyTransactionsRequestBody | undefined>(undefined)
  const currentAbortController = ref<AbortController | null>(null)

  const getCompanyTransactions = async (queryParams?: CompanyTransactionsRequestBody) => {
    if (getCompanyTransactionsPendingPromise.value) {
      const isSameQueryParams = isEqual(queryParams, lastQueryParams.value)

      if (isSameQueryParams) {
        await getCompanyTransactionsPendingPromise.value

        return
      }

      currentAbortController.value?.abort()
      getCompanyTransactionsPendingPromise.value = null
    }

    lastQueryParams.value = queryParams
    currentAbortController.value = new AbortController()

    const promise = executeGetCompanyTransactions(queryParams, {
      signal: currentAbortController.value.signal
    })

    getCompanyTransactionsPendingPromise.value = promise

    try {
      await promise
    } finally {
      if (getCompanyTransactionsPendingPromise.value === promise) {
        getCompanyTransactionsPendingPromise.value = null
      }
    }
  }

  const executeGetCompanyTransactions = async (
    queryParams?: CompanyTransactionsRequestBody,
    options?: { signal?: AbortSignal }
  ) => {
    const employeeStore = useEmployeeStore()

    try {
      if (!employeeStore.employee) {
        await employeeStore.setEmployee()
      }

      if (!employeeStore.employee?.companyId) throw new Error('Company ID not found')

      const response = await getCompanyTransactionsByCompanyId(
        employeeStore.employee.companyId,
        queryParams,
        options?.signal
      )

      if (!response.data) {
        throw new Error('Failed to get company transactions')
      }

      companyTransactions.value = response.data.transactions
    } catch (error) {
      if (axios.isCancel(error)) {
        return
      }

      logger.error(error)

      throw error
    }
  }

  return {
    companyTransactions,
    getCompanyTransactions
  }
})
